import React, { FC } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
  TablePagination,
} from '@mui/material';
import { Visibility, Download } from '@mui/icons-material';
import { ETransactionStatus, ETransactionType, ITransaction, TransactionTypeTranslations } from 'common/transactions.interface';
import { formatDate } from 'utils/date';
import { formatNumberToBRL } from 'utils/formatters';
import { useBankingVisibility } from '../../../contexts/BankingVisibilityContext';
import ProofOfPaymentPixModal from '../modals/ProofOfPaymentPixModal';
import ProofOfPaymentBoletoModal from '../modals/ProofOfPaymentBankSlip';
import ApprovalDetailsModal from '../modals/ApprovalDetailsModal';
import DescriptionModal from '../../../components/DescriptionModal';
import PaymentStatusPill from './PaymentStatusPill';

interface TransactionTableProps {
  transactions: ITransaction[];
  loading: boolean;
  page: number;
  rowsPerPage: number;
  total: number;
  carrierBranchId?: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  onRefresh?: () => void;
}

export const PaymentsTable: FC<TransactionTableProps> = ({
  transactions,
  loading,
  page,
  rowsPerPage,
  total,
  carrierBranchId,
  onPageChange,
  onRowsPerPageChange,
  onRefresh,
}) => {
  const { showValues } = useBankingVisibility();
  const [openPixProofModal, setOpenPixProofModal] = React.useState(false);
  const [openBoletoProofModal, setOpenBoletoProofModal] = React.useState(false);
  const [openApprovalModal, setOpenApprovalModal] = React.useState(false);
  const [openDescriptionModal, setOpenDescriptionModal] = React.useState(false);
  const [selectedTransaction, setSelectedTransaction] = React.useState<ITransaction | undefined>(undefined);

  const columns: {
    label: string;
    field: keyof ITransaction;
    align: 'left' | 'right' | 'center';
    renderCell?: (transaction: ITransaction) => React.ReactNode;
  }[] = [
    {
      label: 'Data',
      field: 'createdAt',
      align: 'center',
      renderCell: (t) => (
        <Typography variant="body2" align="center">
          {formatDate(t.createdAt)}
        </Typography>
      ),
    },
    {
      label: 'Tipo',
      field: 'type',
      align: 'center',
      renderCell: (t) => (
        <Typography variant="body2" align="center">
          {TransactionTypeTranslations[t.type]}
        </Typography>
      ),
    },
    {
      label: 'Valor',
      field: 'value',
      align: 'right',
      renderCell: (t) => (
        <Typography variant="body2" align="right">
          {showValues ? formatNumberToBRL(t.value) : '••••••'}
        </Typography>
      ),
    },
    {
      label: 'Destinatário',
      field: 'counterpartyName',
      align: 'left',
      renderCell: (t) => <Typography variant="body2">{t.counterpartyName || '-'}</Typography>,
    },
    {
      label: 'Status',
      field: 'status',
      align: 'center',
      renderCell: (t) => (
        <Typography variant="body2" align="center">
          <PaymentStatusPill status={t.status} />
        </Typography>
      ),
    },
    {
      label: 'Aprovação',
      field: 'id',
      align: 'center',
      renderCell: (t) => (
        <IconButton
          size="small"
          disabled={!carrierBranchId}
          onClick={() => {
            setSelectedTransaction(t);
            setOpenApprovalModal(true);
          }}
        >
          <Visibility fontSize="small" />
        </IconButton>
      ),
    },
    {
      label: 'Descrição',
      field: 'id',
      align: 'center',
      renderCell: (_t) => (
        <IconButton
          size="small"
          onClick={() => {
            setSelectedTransaction(_t);
            setOpenDescriptionModal(true);
          }}
        >
          <Visibility fontSize="small" />
        </IconButton>
      ),
    },
    {
      label: 'Comprovante',
      field: 'id',
      align: 'center',
      renderCell: (t) => (
        <IconButton
          size="small"
          disabled={t.status !== ETransactionStatus.CONFIRMED || t.type === ETransactionType.EXTERNAL}
          onClick={() => {
            setSelectedTransaction(t);

            if ([ETransactionType.MANUAL_PIX_OUT].includes(t.type)) {
              setOpenPixProofModal(true);
            }

            if ([ETransactionType.BANK_SLIP].includes(t.type)) {
              setOpenBoletoProofModal(true);
            }
          }}
        >
          <Download fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Box
        sx={{
          opacity: loading ? 0.5 : 1,
          pointerEvents: loading ? 'none' : 'auto',
          transition: 'opacity 0.2s ease',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F7F6F6' }}>
                {columns.map((column) => (
                  <TableCell key={column.label} align={column.align}>
                    <Typography variant="body1" fontWeight={500} color="#434241">
                      {column.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id} hover sx={{ borderBottom: '1px solid #E9E8E7' }}>
                  {columns.map((column) => (
                    <TableCell key={column.label} align={column.align}>
                      {column.renderCell?.(transaction)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}

              {!loading && transactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    Nenhuma transação encontrada para o período selecionado.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          onRowsPerPageChange={(e) => {
            onRowsPerPageChange(parseInt(e.target.value, 10));
            onPageChange(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
        />
      </Box>

      <ProofOfPaymentPixModal open={openPixProofModal} onClose={() => setOpenPixProofModal(false)} transaction={selectedTransaction} />

      <ProofOfPaymentBoletoModal
        open={openBoletoProofModal}
        onClose={() => setOpenBoletoProofModal(false)}
        transaction={selectedTransaction}
      />

      <ApprovalDetailsModal
        open={openApprovalModal}
        onClose={() => setOpenApprovalModal(false)}
        transaction={selectedTransaction}
        onApprovalChange={onRefresh}
      />

      <DescriptionModal
        open={openDescriptionModal}
        onClose={() => setOpenDescriptionModal(false)}
        description={selectedTransaction?.description || ''}
      />
    </>
  );
};
